import * as THREE from 'three';
import diameterOrDiagonal from '@/functions/shaft3dExtra';

// Base configuration for mesh material
export const materialConfig = {
  base: {
    color: 0xaaaaaa,
    transparent: true,
    opacity: 0.8,
  },
  ghost: {
    color: 0x4B4B4B,
    opacity: 0.5
  },
  conveyance: {
    color: 0x282728,
    opacity: 1
  },
  crosshairs: {
    color: 0xd5d4d6
  },
  colours: {
    eaimmsBlue: 0x29A7F4
  }
};

let conditionRatingColors = {
  0: 0x6D7372,
  1: 0x54755E,
  2: 0x92A335,
  3: 0xE89C00,
  4: 0xEB6C0E,
  5: 0xE9413A,
};

/**
 * Shaft base constructor
 */
export class ShaftBase {
  constructor(dimensions, color) {
    let SHAFT;
    if (Object.hasOwn(dimensions, 'diameter')) {
      // Set the radius of the circle
      const radius = dimensions.diameter / 2;
      SHAFT = new THREE.Mesh(
        new THREE.CircleGeometry(radius, 120),
        new THREE.MeshBasicMaterial({
          color: color,
          side: THREE.DoubleSide,
        })
      );
      // Rotate the circle so it can represent a shaft-base
      SHAFT.rotation.set(-Math.PI / 2, 0, 0);
    } else {
      SHAFT = new THREE.Mesh(
        // BoxGeometry(w, h, l)
        new THREE.BoxGeometry(dimensions.width, 0, dimensions.length),
        new THREE.MeshBasicMaterial({
          color: color,
          side: THREE.DoubleSide,
        })
      );
    }

    SHAFT.userData.type = 'shaft base';

    return SHAFT;
  }
}

/**
 * Sets new sidewall geometry
 * @param {number | string} thetaStart - the starting point of the side wall
 * @param {number | string} thetaLength - the end point of the sidewall
 */
export function setSideWallGeometry(thetaStart, thetaLength) {
  return new THREE.CylinderGeometry(1, 1, 1, 120, 120, true, Number(thetaStart), Number(thetaLength));
}

/**
 * Builds and returns the correct 3d object based on the element_type
 * @param {object} element - the element data object
 */
function setMeshAndMaterial(element, shaft) {
  switch (element.element_type) {
    // Sidewall elements
    case 'sidewalls':
      return sidewall(element, shaft);
    // Cylindrical elements
    case 'pipes':
    case 'cables':
      return new THREE.Mesh(
        new THREE.CylinderGeometry(1, 1, 1, 120),
        new THREE.MeshLambertMaterial(materialConfig.base)
      );
    // All other elements
    default:
      return new THREE.Mesh(
        new THREE.BoxGeometry(1, 1, 1),
        new THREE.MeshLambertMaterial(materialConfig.base)
      );
  }
}

/**
 * Sidewall method
 */
function sidewall(element, shaft) {
  const isCircleBase = Object.hasOwn(shaft.dimensions, 'diameter');
  /**
   * Don't show box sidewalls on a circle base shaft
   */
  if (isCircleBase) {
    return new THREE.Mesh(
      setSideWallGeometry(
        element.position.thetaStart,
        element.position.thetaLength
      ),
      new THREE.MeshLambertMaterial({
        ...materialConfig.base,
        side: THREE.DoubleSide,
      })
    );
  }
  /**
   * Don't show circle sidewalls on a box base shaft.
   */
  else {
    return new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial(materialConfig.base)
    );
  }
}

/**
 * Shaft Element constructor
 */
export class ShaftElement {
  constructor(element, shaft) {
    if (!element?.geometry) return;
    const { width, height, length } = element.geometry;
    const { x, y, z, thetaStart, thetaLength } = element.position;

    const shaftElement = setMeshAndMaterial(element, shaft);
    if (!shaftElement) return;
    shaftElement.position.set(x, y, z);
    shaftElement.scale.x = width;
    shaftElement.scale.y = height;
    shaftElement.scale.z = length;
    shaftElement.name = element.name;
    shaftElement.userData = setElementUserData(element, shaft);

    if (element.element_type === 'skips_and_cages') {
      shaftElement.userData.originalColor = materialConfig.conveyance.color;
      shaftElement.material.color.set(materialConfig.conveyance.color);
      shaftElement.material.opacity = materialConfig.conveyance.opacity;
    }

    if (element.element_type.includes('sidewalls') && shaft.shape.includes('circle')) {
      shaftElement.position.set(0, shaft.set_spacing / 2, 0);
      shaftElement.position.thetaStart = thetaStart;
      shaftElement.position.thetaLength = thetaLength;
      shaftElement.scale.x = shaft.dimensions.diameter / 2;
      shaftElement.scale.y = shaft.set_spacing;
      shaftElement.scale.z = shaft.dimensions.diameter / 2;
    }

    return shaftElement;
  }
}

/**
 * Issue Crosshairs
 */
export class IssuePointCrosshair {
  constructor(element, shaft) {

    // set up material
    const crosshairMaterial = new THREE.LineBasicMaterial({
      color: materialConfig.crosshairs.color,
    });

    // vertical and horizontal line points
    const pointValue = diameterOrDiagonal(shaft.dimensions, 2, 2, 2);
    const points = {
      horizontal: [
        new THREE.Vector3(-pointValue * 50, 0, 0),
        new THREE.Vector3(pointValue * 50, 0, 0)
      ],
      vertical: [
        new THREE.Vector3(0, -pointValue * 50, 0),
        new THREE.Vector3(0, pointValue * 50, 0)
      ]
    };

    // construct horizontal line
    const crosshairHorizontalLine = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points.horizontal),
      crosshairMaterial
    );

    // construct vertical line
    const crosshairVerticalLine = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points.vertical),
      crosshairMaterial
    );

    // add lines to element (issue point) to create crosshair
    element.add(crosshairHorizontalLine, crosshairVerticalLine);
  }
}

/**
 * Issue constructor
 */
export class IssuePoint {
  constructor(issue) {
    let issuePoint = new THREE.Mesh(
      new THREE.SphereGeometry(1, 32, 16),
      new THREE.MeshBasicMaterial({ color: conditionRatingColors[issue?.overall_condition_rating] || 0xffffff })
    );
    issuePoint.name = issue?.title || 'new point';
    issuePoint.userData.type = 'point';
    issuePoint.userData.id = issue?.id || 'new_point_id';
    issuePoint.userData.overall_condition_rating = issue?.overall_condition_rating || null;
    return issuePoint;
  }
}

/**
 * Positions and scales the issue point onto the parent element
 * @param {object} parentElement - the parent element
 * @param {object} point - the new point
 * @param {object} dimensions - the shaft dimensions of the shaft { diameter, width, length }
 * @param {object} position - the x,y,z position for the new point
 */
export function handlePointPlacement(parentElement, point, dimensions, position) {
  point = scaleIssuePoint(parentElement, point, dimensions);

  for (const [key, value] of Object.entries(position)) {
    point.position[key] = value;
  }

  parentElement.add(point);
}

/**
 * Scales the issue point correctly
 * @param {object} parentElement - the parent element
 * @param {object} point - the new point
 * @param {object} dimensions - the shaft dimensions of the shaft { diameter, width, length }
 * @returns {object} point
 */
export function scaleIssuePoint(parentElement, point, dimensions) {
  for (const key of Object.entries(point.scale)) {
    point.scale[key[0]] = (((1 / parentElement.scale[key[0]]) * 0.5) / 100) * diameterOrDiagonal(dimensions, 2, 2, 2);
  }

  return point;
}

/**
 * Returns userData
 * @param { object } element - a single shaft element data object from the API
 */
function setElementUserData(element, shaft) {
  let isSidewall = element.element_type.includes('sidewalls') && Object.hasOwn(shaft.dimensions, 'diameter');

  const { width, height, length } = element.geometry;
  const { x, y, z, thetaStart, thetaLength } = element.position;

  return {
    id: element.id,
    type: element.element_type,
    group: element.shaft_element_group?.name || null,
    element: true,
    inspectable: element.inspectable,
    originalColor: materialConfig.base.color,
    originalPosition: {
      x: isSidewall ? 0 : x,
      y: isSidewall ? shaft.set_spacing / 2 : y,
      z: isSidewall ? 0 : z,
      width: isSidewall ? 0 : width,
      height: isSidewall ? shaft.set_spacing : height,
      length: isSidewall ? 0 : length,
      thetaStart: thetaStart,
      thetaLength: thetaLength,
    },
  };
}
